import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, DollarSign, ClipboardCheck, AlertTriangle, TrendingUp, BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StatCard } from '../../../shared/components/StatCard';
import { useAuthStore } from '../../../stores/authStore';
import { useModuleStore } from '../../../stores/moduleStore';
import { api, formatUGX } from '../../../lib/api';
import { EnrollStudentModal } from '../../academic/components/EnrollStudentModal';

function useCurrentTerm() {
  return useQuery({
    queryKey: ['current-term'],
    queryFn: () => api.get('/academic/terms/current').then(r => r.data.data),
    retry: false,
  });
}

function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const [students, invoices] = await Promise.allSettled([
        api.get('/academic/students?limit=1').then(r => r.data.meta?.total ?? 0),
        api.get('/finance/invoices?limit=1').then(r => r.data.data),
      ]);
      return {
        total_students: students.status === 'fulfilled' ? students.value : 0,
        invoices_data:  invoices.status === 'fulfilled'  ? invoices.value  : null,
      };
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function DashboardPage() {
  const { t } = useTranslation(['common', 'finance']);
  const user = useAuthStore(s => s.user);
  const tenant = useAuthStore(s => s.tenant);
  const isEnabled = useModuleStore(s => s.isEnabled);
  const [showEnroll, setShowEnroll] = useState(false);

  const { data: term } = useCurrentTerm();
  const { data: stats, isLoading, refetch: refetchStats } = useDashboardStats();

  const hour = new Date().getHours();
  const greetingKey = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const greeting = t(`common:greeting.${greetingKey}`);

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1>{greeting}, {user?.first_name ?? 'there'}</h1>
          {term && (
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 3 }}>
              {term.name} · {term.academic_year?.name ?? ''}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {isEnabled('academic') && (
            <button onClick={() => setShowEnroll(true)} className="btn btn-primary btn-sm">
              <Users size={13} /> New Student
            </button>
          )}
        </div>
      </div>

      {/* KPI grid */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        {isEnabled('academic') && (
          <div className="dash-fade" style={{ animationDelay: '60ms' }}>
            <StatCard
              title={t('common:totalStudents')}
              value={isLoading ? '…' : (stats?.total_students ?? 0).toLocaleString()}
              icon={Users}
              variant="accent"
              loading={isLoading}
            />
          </div>
        )}

        {isEnabled('finance') && (
          <>
            <div className="dash-fade" style={{ animationDelay: '120ms' }}>
              <StatCard
                title={t('finance:dashboard.feesCollectedTerm')}
                value={isLoading ? '…' : formatUGX(0)}
                subtext={t('finance:dashboard.currentTerm')}
                icon={DollarSign}
                variant="green"
                loading={isLoading}
              />
            </div>
            <div className="dash-fade" style={{ animationDelay: '180ms' }}>
              <StatCard
                title={t('finance:dashboard.outstanding')}
                value={isLoading ? '…' : formatUGX(0)}
                subtext={t('finance:dashboard.unpaidBalance')}
                icon={AlertTriangle}
                variant="red"
                loading={isLoading}
              />
            </div>
          </>
        )}

        {isEnabled('academic') && (
          <div className="dash-fade" style={{ animationDelay: '240ms' }}>
            <StatCard
              title={t('common:nav.attendance')}
              value="—"
              subtext={t('finance:dashboard.currentTerm')}
              icon={ClipboardCheck}
              loading={isLoading}
            />
          </div>
        )}
      </div>

      {/* Quick link cards */}
      <div className="dash-fade" style={{ animationDelay: '320ms' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 20 }}>
          {isEnabled('academic') && (
            <QuickLinkCard
              title={t('common:nav.academics')}
              links={[
                { label: t('common:nav.students'),   href: '/students/new' },
                { label: t('common:nav.attendance'), href: '/academic/attendance' },
                { label: 'View all students',        href: '/students' },
              ]}
              icon={BookOpen}
            />
          )}

          {isEnabled('finance') && (
            <QuickLinkCard
              title={t('common:nav.finance')}
              links={[
                { label: t('common:nav.invoices'),       href: '/finance/invoices' },
                { label: t('finance:dashboard.title'),   href: '/finance' },
              ]}
              icon={DollarSign}
            />
          )}

          {isEnabled('hr') && (
            <QuickLinkCard
              title={t('common:nav.hr')}
              links={[
                { label: t('common:nav.staff'),       href: '/hr/staff' },
                { label: t('common:nav.payrollRuns'), href: '/hr/payroll' },
              ]}
              icon={TrendingUp}
            />
          )}
        </div>
      </div>

      {/* Tenant info banner */}
      {tenant && (
        <div className="dash-fade card" style={{ animationDelay: '400ms', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'var(--accent-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <BookOpen size={18} color="var(--p600)" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{tenant.name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2, textTransform: 'capitalize' }}>
              {tenant.school_type?.replace(/_/g, ' ')} · {tenant.pricing_tier} {t('common:plan')}
            </div>
          </div>
        </div>
      )}

      {showEnroll && (
        <EnrollStudentModal
          onClose={() => setShowEnroll(false)}
          onSuccess={() => refetchStats()}
        />
      )}
    </div>
  );
}

function QuickLinkCard({ title, links, icon: Icon }: {
  title: string;
  links: { label: string; href: string }[];
  icon: React.ElementType;
}) {
  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: 'var(--accent-bg)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={15} color="var(--p600)" />
        </div>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</h3>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {links.map(l => (
          <Link
            key={l.href}
            to={l.href}
            style={{ fontSize: 13, color: 'var(--accent)', textDecoration: 'none' }}
            onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = 'var(--accent-2)')}
            onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'var(--accent)')}
          >
            {l.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
